import { SITE_URL } from "@/lib/seo";
import { useEffect, useState } from "react";
import { Share2, MessageCircle, Copy, Gift } from "lucide-react";
import { copyText } from "@/lib/clipboard";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BASE = "I'm on Pula 🌧️ — safe connection, fair trade, honest work & free learning. No fees, you own everything.";

async function resolveLink() {
  try {
    const { data } = await api.get("/referrals/me");
    if (data?.code) return { url: `${SITE_URL}/auth?ref=${data.code}`, referral: true };
  } catch { /* not signed in — share the plain app link */ }
  return { url: SITE_URL, referral: false };
}

function buildText(url, referral) {
  return referral
    ? `${BASE} Join with my link and I'll be recognised for bringing you home: ${url}`
    : `${BASE} Join me: ${url}`;
}
function shareTitle(referral) {
  return referral ? `${BASE} Join with my link and I'll be recognised for bringing you home:` : BASE;
}

export async function sharePulaNow() {
  const { url, referral } = await resolveLink();
  if (navigator.share) {
    try { await navigator.share({ title: "Pula — Rain of Blessings", text: shareTitle(referral), url }); return; }
    catch { return; }
  }
  window.open(`https://wa.me/?text=${encodeURIComponent(buildText(url, referral))}`, "_blank");
}

export const SharePulaButton = ({ testId = "share-pula", className = "" }) => {
  const { user } = useAuth();
  const [link, setLink] = useState({ url: SITE_URL, referral: false });

  useEffect(() => {
    let alive = true;
    if (user) resolveLink().then((l) => { if (alive) setLink(l); });
    return () => { alive = false; };
  }, [user]);

  const wa = `https://wa.me/?text=${encodeURIComponent(buildText(link.url, link.referral))}`;
  const native = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Pula — Rain of Blessings", text: shareTitle(link.referral), url: link.url }); } catch { /* cancelled */ } }
    else window.open(wa, "_blank");
  };

  return (
    <div className={`flex flex-wrap items-center justify-center gap-2 ${className}`} data-testid={testId}>
      <button onClick={native} data-testid={`${testId}-native`}
        className="inline-flex items-center gap-1.5 rounded-full bg-pula-green text-white text-sm px-5 py-2.5 hover:bg-pula-greenhover transition-colors">
        <Share2 className="w-4 h-4" /> Share Pula
      </button>
      <a href={wa} target="_blank" rel="noreferrer" data-testid={`${testId}-whatsapp`}
        className="inline-flex items-center gap-1.5 rounded-full border border-[var(--pula-border)] text-sm px-5 py-2.5 hover:border-pula-green transition-colors text-pula-ink">
        <MessageCircle className="w-4 h-4 text-pula-green" /> WhatsApp
      </a>
      <button onClick={() => copyText(link.url, link.referral ? "Referral link copied — share it!" : "Link copied — paste it anywhere.")} data-testid={`${testId}-copy`}
        className="inline-flex items-center gap-1.5 rounded-full border border-[var(--pula-border)] text-sm px-5 py-2.5 hover:border-pula-green transition-colors text-pula-ink">
        <Copy className="w-4 h-4" /> Copy link
      </button>
      {link.referral && (
        <p className="w-full text-xs text-pula-green font-body mt-1 flex items-center justify-center gap-1" data-testid={`${testId}-reward-note`}>
          <Gift className="w-3.5 h-3.5" /> When a friend joins with your link, you'll earn a thank-you badge — recognition, not cash.
        </p>
      )}
    </div>
  );
};
